import {
  AnalyticsEvents,
  setIsOverlayLogin,
  useAppDispatch,
  useTranslatedSubscriptionCardFeatures,
  useTranslation,
} from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Icon, IconSize, IconVariants, useMedia } from "@next-platform/client-common-spicy-ui";
import { Card, CardBody, Spacer } from "@nextui-org/react";
import { useNavigate } from "react-router-dom";

import { LocalizedLink } from "@/components";
import { Col, Row, SpicyButton, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, POPULAR_PLAN, SUBSCRIBE_USING_CRYPTO_PAGE_ROUTE, USER_ROLES } from "@/constants";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import useAuth from "@/services/useAuth";
import { SubscriptionCardProps } from "@/types";
import { cn, getCookie, getCryptoPaymentLink, getCurrencySymbol, getPurchaseData, isSaleLive, onSubscribeSticky } from "@/utils";

import getSubscriptionCardLabel from "../utils/getSubscriptionCardLabel";

const COUNTRY = getCookie("country");

export default function SubscriptionCard(props: SubscriptionCardProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { coupon, planData: subscriptionCardData, purchaseOptions } = props;
  const { tier, price } = getPurchaseData(subscriptionCardData);
  const { role, period, plan_id: planId } = subscriptionCardData;

  const features = useTranslatedSubscriptionCardFeatures(role);
  const frequencyLabel = getSubscriptionCardLabel(period, t);
  const isMostPopular = role === POPULAR_PLAN;
  const navigate = useNavigate();
  const { isMobile } = useMedia();
  const { pagePath } = useLocalizedLocation();
  const { logEvent } = useQueueLogEvent();

  const isSubsribeUsingCryptoPage = pagePath === SUBSCRIBE_USING_CRYPTO_PAGE_ROUTE;
  const mobileColumnGap = 0.5;
  const currencyCode = subscriptionCardData?.currency || COUNTRY;
  const currencySymbol = getCurrencySymbol(currencyCode);
  const dispatch = useAppDispatch();
  const applicationId = basicAppConfig.applicationId;
  const isCryptoLink = isSubsribeUsingCryptoPage;
  const docsUrl = EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs;
  const learnMoreLinks = {
    [USER_ROLES.MINIMAL]: `${docsUrl}/product-guides/premium-features#get-a-taste-tier`,
    [USER_ROLES.BASIC]: `${docsUrl}/product-guides/premium-features#true-supporter-tier`,
    [USER_ROLES.PREMIUM]: `${docsUrl}/product-guides/premium-features#im-all-in-tier`,
  };

  const handleSubscriptionClick = () => {
    if (!isAuthenticated) {
      return dispatch(setIsOverlayLogin(true));
    }

    logEvent(AnalyticsEvents.SUBSCRIPTION_PLAN_SELECT, { plan_id: planId, plan_name: tier });
    logEvent(AnalyticsEvents.SUBSCRIPTION_MONEY_VALUE, { currency: currencyCode, value: price });

    if (isSubsribeUsingCryptoPage) {
      return;
    }
    const route = onSubscribeSticky(planId, coupon);
    navigate(route, { state: { purchaseOptions } });
  };

  const trackPlanView = () => {
    logEvent(AnalyticsEvents.SUBSCRIPTION_PLAN_VIEW, { plan_id: planId, plan_name: tier });
  };

  return (
    <Card
      className={
        "border-solid border-1 rounded-xl overflow-hidden bg-transparent cursor-default w-[350px] max-[400px]:w-full " +
        (isMostPopular ? "border-blue-600" : "")
      }
      isPressable
    >
      <CardBody className={`p-0 ${isMobile && isMostPopular && "bg-blue-600 bg-opacity-10"}`}>
        {isMostPopular ? (
          <Row className={"py-2 justify-center " + (isMobile ? "border-0 border-b-1 border-solid border-blue-600 *:text-blue-600" : "bg-blue-600")}>
            <Text variant="lg" type="medium white">
              <Trans i18nKey="subscription:page.subscriptionCard.mostPopular" />
            </Text>
          </Row>
        ) : null}
        <Col className={isMobile ? "px-3 pt-4 pb-2" : "px-5 pt-8 pb-2 min-h-[620px]"} gap={isMobile ? mobileColumnGap : 1}>
          <Col gap={isMobile ? mobileColumnGap : 1} className={isMobile ? "pl-3" : undefined}>
            <Text variant={isMobile ? "xxl" : "large"} type={isMobile ? "font-medium" : "font-bold"}>
              {subscriptionCardData.name || tier}
            </Text>
            <Row align="center">
              <Text variant={isMobile ? "xxl" : "large"} type={isMobile ? "font-medium" : "font-bold"}>
                {isSaleLive() ? "Try for " : null} {currencySymbol}
                {price}
              </Text>
              <Text variant="md" type={"pt-1.5 " + (isMobile && "opacity-60")}>
                /{isMobile ? "per" : null} {frequencyLabel}
              </Text>
            </Row>
          </Col>
          {isMobile ? <Spacer y={0.5} /> : null}
          <SpicyButton
            as={isCryptoLink ? LocalizedLink : undefined}
            to={isCryptoLink ? getCryptoPaymentLink(subscriptionCardData) : undefined}
            color={isMostPopular ? "primary_strong" : "transparent_bordered"}
            radius="sm"
            onClick={handleSubscriptionClick}
            target={isCryptoLink ? "_blank" : undefined}
          >
            <Text b type={isMostPopular ? "white" : undefined}>
              <Trans i18nKey="subscription:page.subscriptionCard.buttonPaid" />
            </Text>
          </SpicyButton>
          {isMobile ? <Spacer /> : null}
          <Col gap={1}>
            <Col className="gap-4 max-mob:gap-3">
              {features?.map((feature: string, idx: number) => {
                const isIncludesLine = idx === 0;

                return (
                  <Row key={idx} align="center">
                    {isIncludesLine ? null : (
                      <Icon
                        icon={IconVariants.CHECK}
                        className={"relative top-0.5 mr-2 max-mob:text-blue-600"}
                        size={isMobile ? IconSize.SMALL : IconSize.MEDIUM}
                      />
                    )}
                    <Text variant={isMobile ? "sm" : "md"} className={cn("max-mob:opacity-60", isIncludesLine ? "italic" : "capitalize")}>
                      {feature}
                    </Text>
                  </Row>
                );
              })}
            </Col>
          </Col>

          <LocalizedLink
            className={"mt-auto opacity-60 underline py-2 text-white " + (isMobile ? "justify-center text-[12px]" : "text-[16px]")}
            to={learnMoreLinks[role as keyof typeof learnMoreLinks]}
            target="_blank"
            aria-label={`learn-more-about-${tier}-tier`}
            onClick={trackPlanView}
          >
            <Trans i18nKey="subscription:page.subscriptionCard.learnMoreAboutTier" values={{ tier }} />
          </LocalizedLink>
        </Col>
      </CardBody>
    </Card>
  );
}
